import React, { useEffect, useState } from 'react';
import {
  Card, Tag, Button, Space, Popconfirm, message, Select, Typography, Tooltip, Empty, Pagination, Input,
} from 'antd';
import {
  CheckCircleOutlined, StopOutlined, DeleteOutlined, DownOutlined,
} from '@ant-design/icons';
import { getDevices, approveDevice, blockDevice, deleteDevice } from '../services/api';
import { formatDateTime } from '../utils/format';

const { Title, Text } = Typography;

const STATUS_MAP = {
  pending: { text: '待审批', color: 'orange' },
  active: { text: '已授权', color: 'green' },
  blocked: { text: '已封锁', color: 'red' },
};

export default function DeviceManagement() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [userKeyword, setUserKeyword] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [expandedDeviceIds, setExpandedDeviceIds] = useState([]);

  const fetchDevices = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const res = await getDevices(params);
      setData(res.data.data || []);
    } catch (err) {
      message.error('获取设备列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    setExpandedDeviceIds([]);
  }, [userKeyword]);

  const handleApprove = async (id) => {
    try {
      await approveDevice(id);
      message.success('设备已授权');
      fetchDevices();
    } catch (err) {
      message.error('授权失败');
    }
  };

  const handleBlock = async (id) => {
    try {
      await blockDevice(id);
      message.success('设备已封锁');
      fetchDevices();
    } catch (err) {
      message.error('封锁失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDevice(id);
      message.success('设备记录已删除');
      fetchDevices();
    } catch (err) {
      message.error('删除失败');
    }
  };

  const formatDetailValue = (value) => (
    value === null || value === undefined || value === '' ? '-' : value
  );

  const formatDeviceMemory = (value) => {
    const formatted = formatDetailValue(value);
    return formatted === '-' ? '浏览器未提供' : `${formatted} GB`;
  };

  const toggleExpanded = (id) => {
    setExpandedDeviceIds((prev) => (
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    ));
  };

  const expandedRowRender = (record) => {
    const detailItems = [
      { label: '平台架构', value: record.platform },
      { label: 'CPU 核心数', value: record.cpu_cores },
      { label: '设备内存', value: formatDeviceMemory(record.memory) },
      { label: '最大触摸点数', value: record.max_touch_points },
      { label: 'Canvas 指纹', value: record.canvas_fp, mono: true },
    ];
    const graphicsItems = [
      { label: '显卡厂商', value: record.webgl_vendor },
      { label: '显卡型号', value: record.webgl_renderer },
    ];

    return (
      <div className="device-detail-panel">
        <div className="device-detail-highlight">
          <span className="device-detail-label">设备指纹</span>
          <code className="device-detail-code">{formatDetailValue(record.fingerprint)}</code>
        </div>

        <div className="device-detail-grid">
          {detailItems.map((item) => (
            <div className="device-detail-item" key={item.label}>
              <span className="device-detail-label">{item.label}</span>
              <strong className={`device-detail-value${item.mono ? ' device-detail-value--mono' : ''}`}>
                {formatDetailValue(item.value)}
              </strong>
            </div>
          ))}
        </div>

        <div className="device-detail-stack">
          {graphicsItems.map((item) => (
            <div className="device-detail-wide" key={item.label}>
              <span className="device-detail-label">{item.label}</span>
              <strong className="device-detail-value">{formatDetailValue(item.value)}</strong>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderDeviceActions = (record) => (
    <Space size={4} wrap>
      {record.status === 'pending' && (
        <Tooltip title="授权">
          <Button
            type="primary"
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() => handleApprove(record.id)}
          />
        </Tooltip>
      )}
      {record.status === 'active' && (
        <Tooltip title="封锁">
          <Button
            danger
            size="small"
            icon={<StopOutlined />}
            onClick={() => handleBlock(record.id)}
          />
        </Tooltip>
      )}
      {record.status === 'blocked' && (
        <Tooltip title="解封">
          <Button
            type="primary"
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() => handleApprove(record.id)}
          />
        </Tooltip>
      )}
      <Popconfirm
        title="确认删除此设备记录？"
        onConfirm={() => handleDelete(record.id)}
        okText="删除"
        cancelText="取消"
      >
        <Button size="small" icon={<DeleteOutlined />} danger ghost title="删除" />
      </Popconfirm>
    </Space>
  );

  const renderSummaryItem = (label, value, mono = false) => (
    <div className="device-summary-item">
      <span>{label}</span>
      <strong className={mono ? 'device-summary-value--mono' : undefined}>
        {formatDetailValue(value)}
      </strong>
    </div>
  );

  const sortDevicesForReview = (devices) => {
    const statusPriority = { pending: 0, active: 1, blocked: 2 };
    return [...devices].sort((a, b) => {
      const statusDiff = (statusPriority[a.status] ?? 9) - (statusPriority[b.status] ?? 9);
      if (statusDiff !== 0) {
        return statusDiff;
      }
      return new Date(b.last_seen_at || b.first_seen_at || 0) - new Date(a.last_seen_at || a.first_seen_at || 0);
    });
  };

  const normalizedUserKeyword = userKeyword.trim().toLowerCase();
  const filteredDevices = sortDevicesForReview(data).filter((record) => {
    if (!normalizedUserKeyword) {
      return true;
    }
    return [
      record.user_name,
      record.user_email,
      record.ip,
      record.fingerprint,
      record.browser,
      record.os,
    ].some((value) => String(value || '').toLowerCase().includes(normalizedUserKeyword));
  });

  const totalPages = Math.max(1, Math.ceil(filteredDevices.length / pagination.pageSize));
  const currentPage = Math.min(pagination.current, totalPages);
  const pagedData = filteredDevices.slice(
    (currentPage - 1) * pagination.pageSize,
    currentPage * pagination.pageSize,
  );

  return (
    <div className="page-shell">
      <div className="page-heading">
        <div>
          <div className="page-kicker">Device Approval</div>
          <Title level={3} className="page-heading-title">设备管理</Title>
          <Text className="page-heading-subtitle">审核新设备登录、封锁异常设备并查看设备指纹详情。</Text>
        </div>
        <Space wrap>
          <Input
            className="device-search-input"
            allowClear
            placeholder="搜索用户 / 邮箱 / IP / 指纹"
            value={userKeyword}
            onChange={(e) => setUserKeyword(e.target.value)}
          />
          <span>状态筛选：</span>
          <Select
            placeholder="全部状态"
            allowClear
            style={{ width: 120 }}
            value={statusFilter || undefined}
            onChange={(v) => {
              setStatusFilter(v || '');
              setPagination((prev) => ({ ...prev, current: 1 }));
              setExpandedDeviceIds([]);
            }}
            options={[
              { value: 'pending', label: '待审批' },
              { value: 'active', label: '已授权' },
              { value: 'blocked', label: '已封锁' },
            ]}
          />
          <Button onClick={fetchDevices}>刷新</Button>
        </Space>
      </div>
      <Card className="content-card" loading={loading}>
        {filteredDevices.length === 0 ? (
          <Empty description={data.length === 0 ? '暂无设备记录' : '暂无匹配设备'} />
        ) : (
          <>
            <div className="device-card-list">
              {pagedData.map((record) => {
                const isExpanded = expandedDeviceIds.includes(record.id);
                return (
                  <div className={`device-card${isExpanded ? ' is-expanded' : ''}`} key={record.id}>
                    <div className="device-card-body">
                      <Tooltip title={isExpanded ? '收起详情' : '查看详情'}>
                        <Button
                          className="device-card-expand"
                          type="text"
                          icon={<DownOutlined rotate={isExpanded ? 180 : 0} />}
                          onClick={() => toggleExpanded(record.id)}
                        />
                      </Tooltip>

                      <div className="device-card-identity">
                        <Space size={8} wrap>
                          <Text strong className="device-card-user">{formatDetailValue(record.user_name)}</Text>
                          <Tag color={STATUS_MAP[record.status]?.color}>{STATUS_MAP[record.status]?.text || record.status}</Tag>
                        </Space>
                        <Text className="device-card-email">{formatDetailValue(record.user_email)}</Text>
                        <div className="device-card-pills">
                          <span>{formatDetailValue(record.browser)}</span>
                          <span>{formatDetailValue(record.os)}</span>
                        </div>
                      </div>

                      <div className="device-card-summary">
                        {renderSummaryItem('IP地址', record.ip)}
                        {renderSummaryItem('设备指纹', record.fingerprint, true)}
                        {renderSummaryItem('首次登录', formatDateTime(record.first_seen_at))}
                        {renderSummaryItem('最后登录', formatDateTime(record.last_seen_at))}
                      </div>

                      <div className="device-card-actions">
                        {renderDeviceActions(record)}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="device-card-detail">
                        {expandedRowRender(record)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="device-card-pagination">
              <Pagination
                current={currentPage}
                pageSize={pagination.pageSize}
                total={filteredDevices.length}
                showSizeChanger
                showTotal={(total) => `共 ${total} 台设备`}
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
